import * as React from "react";
import Typography from "@mui/material/Typography";
import useAuth from "./hooks";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import { Eye as EyeIcon } from "@phosphor-icons/react/dist/ssr/Eye";
import { EyeSlash as EyeSlashIcon } from "@phosphor-icons/react/dist/ssr/EyeSlash";
import { Controller, useForm } from "react-hook-form";
import { z as zod } from "zod";
import Link from "@mui/material/Link";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useProfile } from "../profile/hooks";

function Login() {
  const defaultValues = { username: "", password: "" };
  const [showPassword, setShowPassword] = React.useState();
  const navigate = useNavigate();
  const [isPending, setIsPending] = React.useState(false);

  const { login, auth } = useAuth();
  const { loadProfile } = useProfile();

  const postLoginAction = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      const params = {
        username: user.userName,
        roles: user.roles[0].role,
      };

      loadProfile(params);

      const user_role = user.roles[0].role;
      switch (user_role) {
        case "STUDENT":
          navigate("/study", { replace: true });
          break;
        case "CONTENT_ADMIN":
          navigate("/diagnostic", { replace: true });
          break;
        case "TEACHER":
          navigate("/teacher/dashboard", { replace: true });
          break;
      }
    }
  };

  const schema = zod.object({
    username: zod.string().min(1, { message: "Username is required" }),
    password: zod.string().min(1, { message: "Password is required" }),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues, resolver: zodResolver(schema) });

  const onSubmit = (formValues) => {
    login({ formValues, navigate, setIsPending, postLoginAction });
    // navigate("/dashboard");
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={1}>
        <Typography
          variant="h4"
          align="center"
          fontWeight="700"
          sx={{
            fontSize: { xs: "1.6rem", md: "2rem", lg: "2.125rem" },
          }}
        >
          Welcome to Azvasa!
        </Typography>
        <Typography color="primary.orange" align="center" variant="h6">
          Login into your account
        </Typography>
      </Stack>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Controller
            control={control}
            name="username"
            render={({ field }) => (
              <FormControl error={Boolean(errors.username)}>
                <InputLabel>User Name</InputLabel>
                <OutlinedInput {...field} label="User Name" type="text" />
                {errors.username ? (
                  <FormHelperText>{errors.username.message}</FormHelperText>
                ) : null}
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field }) => (
              <FormControl error={Boolean(errors.password)}>
                <InputLabel>Password</InputLabel>
                <OutlinedInput
                  {...field}
                  endAdornment={
                    showPassword ? (
                      <EyeIcon
                        cursor="pointer"
                        fontSize="var(--icon-fontSize-md)"
                        onClick={() => {
                          setShowPassword(false);
                        }}
                      />
                    ) : (
                      <EyeSlashIcon
                        cursor="pointer"
                        fontSize="var(--icon-fontSize-md)"
                        onClick={() => {
                          setShowPassword(true);
                        }}
                      />
                    )
                  }
                  label="Password"
                  type={showPassword ? "text" : "password"}
                />
                {errors.password ? (
                  <FormHelperText>{errors.password.message}</FormHelperText>
                ) : null}
              </FormControl>
            )}
          />
          <div></div>
          {errors.root ? (
            <Alert color="error">{errors.root.message}</Alert>
          ) : null}
          <Button
            disabled={isPending}
            type="submit"
            size="large"
            sx={{ fontSize: "18px" }}
            variant="contained"
          >
            Sign in
          </Button>
        </Stack>
        <Link
          href={`/reset-password`}
          sx={{
            color: "var(--mui-palette-primary-dark)",
            mt: 2,
            display: "inline-block",
          }}
        >
          Forgot password?
        </Link>
      </form>
    </Stack>
  );
}

export default Login;
